import { debounce } from 'lodash';
import { set } from '@vue/composition-api';
import smh from '@/renderer/scripts/smh';
import store from '@/renderer/store';
import { LRUCache } from '@/renderer/scripts/libs/lru/lru';
import { isAllowImageMogr } from '@/renderer/scripts/composition/allowPreview';

type Handler = {
  getImagePre: (
    tableData: {
      list: any[];
    },
    list: RequestItem[],
    firstVisibleIndex: number,
    endVisibleIndex: number,
    visibleSize: number,
    spaceOrgId?: any,
    spaceId?: string,
  ) => void;
  debouncedFetchData: (
    params: { e: WheelEvent },
    rowHeight: number,
    tableData: {
      list: any[];
    },
    list: RequestItem[],
    orgId?: any,
    spaceId?: string,
  ) => Promise<void> | undefined;
};

type RequestItem = {
  fileType: string;
  path: string[];
  crc64: string;
  size?: string;
  visible?: boolean;
  spaceOrgId?: any;
  spaceId?: string;
  imgUrl?: string;
  authorityList?: {
    canPreview?: boolean;
  };
};

class ImagePreviewCache {
  // key : { crc64: string; size?: string; userId: string; orgId: string;}
  // value: {time: number; url: ''}
  public static imageMap: LRUCache<string, string> = new LRUCache(200);
  // 缓存最长不能超过 2 个小时，url 将失效，这里设置 1.8 个小时, 避免边界情况
  public static MAX_TIME = 1.8 * 60 * 60 * 1000;
}

function getShouldRequestList(
  firstVisibleIndex: number,
  endVisibleIndex: number,
  visibleSize: number,
  list: RequestItem[],
  changeList: Map<number, string>,
): RequestItem[] {
  const shouldRequestNetworkList = [];
  let last = endVisibleIndex + visibleSize;
  if (last >= list.length) {
    last = list.length;
  }
  for (let index = firstVisibleIndex; index < last; index++) {
    // 获取当前屏符合条件的，如果当前屏需要请求的数量过少，就添加下一屏的
    const item = list[index];
    const { userId } = store.getters;
    const currentOrgId = store.getters.orgId;
    if (
      item.fileType === 'image' && // 必须是图片才能预览
      item.path && // 有路径
      isAllowImageMogr(item.path.join('/')) && // 万象支持类型
      item.visible !== false && // 可见
      item.authorityList?.canPreview !== false && // 有预览权限
      !item.imgUrl // 没有预览导致
    ) {
      const key = JSON.stringify({
        crc64: item.crc64,
        size: item.size,
        orgId: currentOrgId,
        userId,
      });
      const imageCacheStr = ImagePreviewCache.imageMap.get(key);
      if (imageCacheStr && !item.imgUrl) {
        const imageCache = JSON.parse(imageCacheStr);
        const currentTime = new Date().getTime();
        if (currentTime - imageCache.time > ImagePreviewCache.MAX_TIME) {
          ImagePreviewCache.imageMap.remove(key);
          imageCache.url = '';
          // console.log('cache time out', item.path);
          shouldRequestNetworkList.push(item);
        } else {
          // 如果已经缓存，但是图片为空，则直接赋值， 就不再需要请求了
          changeList.set(index + firstVisibleIndex, imageCache.url);
          item.imgUrl = imageCache.url;
          // console.log('has cache', item.path);
        }
      } else {
        shouldRequestNetworkList.push(item);
      }
    }
    if (
      shouldRequestNetworkList.length >= visibleSize &&
      shouldRequestNetworkList.length >= endVisibleIndex - firstVisibleIndex
    ) {
      break;
    }
  }
  return shouldRequestNetworkList;
}

async function requestNetWorkAndRefresh(
  netWorkList: {
    imgUrl: string;
    spaceId: string | undefined;
    path: string;
    size: string | undefined;
    spaceOrgId: any;
    crc64: string;
    type: string;
  }[],
  tableData: { list: any[] },
) {
  try {
    const { userId } = store.getters;
    const currentOrgId = store.getters.orgId;
    // console.log('request image preview:', netWorkList.length);
    const params = {
      size: '50',
      directoryInfoList: netWorkList,
    };
    const res = await smh.userAPI.getImageThumbnail(params);
    const resultList = res.data.contents;
    for (let i = 0; i < resultList.length; i++) {
      netWorkList[i].imgUrl = resultList[i].cosUrl;
      // 保存到缓存，以便下次使用
      ImagePreviewCache.imageMap.put(
        JSON.stringify({
          crc64: netWorkList[i].crc64,
          size: netWorkList[i].size,
          orgId: currentOrgId,
          userId,
        }),
        JSON.stringify({
          time: new Date().getTime(),
          url: netWorkList[i].imgUrl,
        }),
      );
    }
    tableData.list = tableData.list.map(item => {
      const index = netWorkList.findIndex(value => {
        return value.path === item.path.join('/') && value.crc64 === value.crc64 && value.size === value.size;
      });
      if (index !== -1) {
        set(item, 'imgUrl', netWorkList[index].imgUrl);
      }
      return item;
    });
  } catch (e) {}
}

export default function useImagePreview(): Handler {
  const methods = {
    /**
     * 自动根据当前表格去请求对应的预览图, 注意，该方法要绑定在滚动监听里
     * @param params
     * @param rowHeight item 高度
     * @param tableData table 数据
     * @param list data, 数据
     * @param orgId 组织 id，不传则使用 list 里自带的
     * @param spaceId 空间 id，不传则是所有 list 里自带的
     */
    async autoImagePre(
      params: { e: WheelEvent },
      rowHeight: number,
      tableData: {
        list: any[];
      },
      list: RequestItem[],
      orgId?: any,
      spaceId?: string,
    ) {
      const { target } = params.e;
      const { clientHeight, scrollHeight, scrollTop } = target as HTMLElement;
      // console.log('tableHeight', clientHeight, 'contentHeight', scrollHeight, 'scrollTop', scrollTop);
      let firstVisibleIndex = ~~(scrollTop / rowHeight); // 第一个可见元素的 index
      let endVisibleIndex = ~~((clientHeight + scrollTop) / rowHeight); // 最后一个可见元素的 index
      const visibleSize = ~~(clientHeight / rowHeight + 1); // 可见元素数目， +1 是为了避免边界问题
      // console.log('startIndex', firstVisibleIndex, 'endIndex', endVisibleIndex, 'visibleSize', visibleSize);

      // 上下获取更广的范围(让加载更准备，避免边界问题导致上下两个元素每个加载到)
      firstVisibleIndex -= 1;
      endVisibleIndex += 1;
      if (firstVisibleIndex < 0) {
        firstVisibleIndex = 0;
      }
      if (endVisibleIndex >= list.length) {
        endVisibleIndex = list.length - 1;
      }

      // 请求预览图并刷新
      await methods.getImagePre(tableData, list, firstVisibleIndex, endVisibleIndex, visibleSize, orgId, spaceId);
    },
    async getImagePre(
      tableData: {
        list: any[];
      },
      list: RequestItem[],
      firstVisibleIndex = 0,
      endVisibleIndex: number,
      visibleSize: number,
      spaceOrgId?: any,
      spaceId?: string,
    ) {
      const changeList: Map<number, string> = new Map();
      // 先过滤出需要加载的图片集合
      const shouldRequestNetworkList = getShouldRequestList(
        firstVisibleIndex,
        endVisibleIndex,
        visibleSize,
        list,
        changeList,
      ).map(item => {
        return {
          // 如果 item 没有组织 id 和 space id，就从传入的取
          spaceOrgId: item.spaceOrgId ? item.spaceOrgId : spaceOrgId,
          spaceId: item.spaceId ? item.spaceId : spaceId,
          crc64: item.crc64,
          size: item.size,
          path: item.path.join('/'),
          type: 'file',
          imgUrl: '',
        };
      });
      if (shouldRequestNetworkList.length === 0) {
        if (changeList.size === 0) {
          // console.log('no need request');
          // 没有数据变化，不处理
          return;
        }
        // console.log('not network request, has cache');
        // 不需要请求网络，但是缓存里有，网址需要更新
        tableData.list = list.map((item, index) => {
          const url = changeList.get(index);
          if (url !== undefined) {
            item.imgUrl = url;
          }
          return item;
        });
        return;
      }

      // 分组请求，加快速度
      const step = 5;
      const arrays = [];
      for (let i = shouldRequestNetworkList.length; i > 0; i = i - step) {
        arrays.push(i);
      }
      // 使用倒序，让最少的组在最上面，最开始请求
      arrays.reverse().forEach(index => {
        // console.log('start', Math.max(index - step, 0), 'end', index);
        requestNetWorkAndRefresh(shouldRequestNetworkList.slice(Math.max(index - step, 0), index), tableData);
      });
    },
  };
  const debouncedFetchData = debounce(methods.autoImagePre, 400);
  return { getImagePre: methods.getImagePre, debouncedFetchData };
}
